import { useTranslation } from "react-i18next";
import i18n from "i18n";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardTitle, Col, Input, Row, Table } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import {
  AddFromBeverageCatalogAPI,
  AddMenuItemAPI,
  DeleteMenuItemAPI,
  GetMenuItemsAPI,
  SearchBeverageCatalogAPI,
  UpdateMenuItemAPI,
} from "network/menuItems";

// Sezioni della carta diverse dal vino: ognuna con i suoi campi (niente attributi del vino).
export const SECTION_INFO = {
  food: { kcal: true, catalog: false },
  beer: { kcal: false, catalog: true },
  beverages: { kcal: false, catalog: true },
  waters: { kcal: false, catalog: true },
  drinks: { kcal: false, catalog: true },
  spirits: { kcal: false, catalog: true },
  mixers: { kcal: false, catalog: true },
};

export const ALLERGENS = [
  "gluten", "crustaceans", "eggs", "fish", "peanuts", "soy", "milk", "nuts", "celery", "mustard", "sesame", "sulphites", "lupin", "molluscs",
];

const eur = (n) => `${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const EMPTY = { name: "", price: "", category: "", description: "", allergens: [], kcal: "", image: null, removeImage: false };
// Le foto arrivano con un percorso relativo al backend (/api/...): lo completa con l'indirizzo del backend.
const API_ORIGIN = (() => {
  try {
    return new URL(process.env.REACT_APP_BASE_URL).origin;
  } catch (e) {
    return "";
  }
})();
const imgUrl = (src) => (src && src.startsWith("/") ? `${API_ORIGIN}${src}` : src);
const errText = (e) => e?.message || e?.data?.message || i18n.t("products:menu.failed");

const SectionMenuItems = ({ sectionData, mode }) => {
  const { t } = useTranslation("products");
  const code = (sectionData?.name || "").toLowerCase();
  const info = SECTION_INFO[code] || SECTION_INFO.food;
  const secKey = SECTION_INFO[code] ? code : "food";
  const secTitle = t(`menu.sections.${secKey}.title`);
  const item = t(`menu.sections.${secKey}.item`);
  const catPlaceholder = t(`menu.sections.${secKey}.catPlaceholder`);
  const navigate = useNavigate();
  const base = `/${code}/products`;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState(mode === "add" ? { ...EMPTY } : null);
  const [saving, setSaving] = useState(false);
  // catalogo Beverage Data
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [prices, setPrices] = useState({});

  const load = () =>
    GetMenuItemsAPI()
      .then((r) => setItems((r?.data?.dishes || []).filter((d) => d.section === code)))
      .catch((e) => ErrorToaster(errText(e)))
      .finally(() => setLoading(false));

  useEffect(() => {
    setLoading(true);
    setForm(mode === "add" ? { ...EMPTY } : null);
    setQ("");
    setFound([]);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, mode]);

  useEffect(() => {
    if (!info.catalog || q.trim().length < 2) return setFound([]);
    const timer = setTimeout(() => {
      SearchBeverageCatalogAPI(q.trim(), code).then((r) => setFound(r?.data?.beverages || [])).catch(() => setFound([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [q, code, info.catalog]);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category).filter(Boolean))].sort(), [items]);
  const shown = items.filter((i) => !filter || `${i.name} ${i.category || ""}`.toLowerCase().includes(filter.toLowerCase()));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fromCash = form?.source === "cic";

  const edit = (d) => {
    setForm({ ...EMPTY, id: d.uuid, source: d.source, name: d.name, price: d.price ?? "", category: d.category || "",
      description: d.description || "", allergens: d.allergens || [], kcal: d.kcal ?? "", currentImage: d.image, fromCatalog: !!d.beverage_dataset });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const close = () => {
    setForm(null);
    if (mode === "add") navigate(base);
  };

  const save = async () => {
    if (!form.name.trim()) return ErrorToaster(t("menu.enterName"));
    if (!fromCash && String(form.price).trim() === "") return ErrorToaster(t("menu.enterPrice"));
    const fd = new FormData();
    if (!fromCash) {
      fd.append("name", form.name.trim());
      fd.append("price", String(form.price).replace(",", "."));
    }
    fd.append("section", code);
    fd.append("category", form.category.trim());
    fd.append("description", form.description.trim());
    fd.append("allergens", form.allergens.join(","));
    if (info.kcal) fd.append("kcal", form.kcal === "" ? "" : String(form.kcal));
    if (form.image) fd.append("image", form.image);
    if (form.removeImage && !form.image) fd.append("remove_image", "true");
    setSaving(true);
    try {
      if (form.id) await UpdateMenuItemAPI(form.id, fd);
      else await AddMenuItemAPI(fd);
      successToaster(form.id ? t("menu.saved") : t("menu.itemAdded", { item: `${item[0].toUpperCase()}${item.slice(1)}` }));
      setForm(null);
      if (mode === "add") navigate(base);
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (d) => {
    try {
      await UpdateMenuItemAPI(d.uuid, { is_available: !d.is_available });
      successToaster(d.is_available ? t("menu.soldOutDone") : t("menu.availableAgain"));
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  const remove = async (d) => {
    if (!window.confirm(t("menu.confirmRemove", { name: d.name }))) return;
    try {
      await DeleteMenuItemAPI(d.uuid);
      successToaster(t("menu.removed"));
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };
  const addFromCatalog = async (b) => {
    const price = String(prices[b.uuid] ?? "").replace(",", ".");
    if (!price) return ErrorToaster(t("menu.enterSalePrice"));
    try {
      await AddFromBeverageCatalogAPI({ beverage_dataset: b.uuid, price });
      successToaster(t("menu.addedToList", { name: b.name }));
      setQ("");
      setFound([]);
      load();
    } catch (e) {
      ErrorToaster(errText(e));
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          {info.catalog && !form && (
            <Card>
              <CardBody>
                <CardTitle tag="h4">{t("menu.catalogTitle")}</CardTitle>
                <p className="text-muted small mb-2">
                  {t("menu.catalogHint")}
                </p>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("menu.catalogSearch")} style={{ maxWidth: 420 }} />
                {q.trim().length >= 2 && (
                  <div className="mt-2">
                    {found.length === 0 ? (
                      <p className="text-muted small mb-0">
                        {t("menu.catalogNone", { item })}
                      </p>
                    ) : (
                      <Table size="sm" className="mb-0">
                        <tbody>
                          {found.map((b) => (
                            <tr key={b.uuid}>
                              <td>
                                <b>{b.name}</b>
                                <div className="small text-muted">
                                  {[b.brand, b.volume_cl ? `${b.volume_cl} cl` : "", b.abv ? `${b.abv}%` : "", b.ean].filter(Boolean).join(" · ")}
                                </div>
                              </td>
                              <td style={{ width: 130 }}>
                                <Input bsSize="sm" inputMode="decimal" placeholder={t("menu.pricePlaceholder")} value={prices[b.uuid] ?? ""}
                                  onChange={(e) => setPrices((p) => ({ ...p, [b.uuid]: e.target.value }))} />
                              </td>
                              <td style={{ width: 110 }} className="text-right">
                                <Button size="sm" color="primary" onClick={() => addFromCatalog(b)}>{t("menu.add")}</Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {form && (
            <Card>
              <CardBody>
                <CardTitle tag="h4">{form.id ? t("menu.editTitle", { item }) : t("menu.newTitle", { item })}</CardTitle>
                {fromCash && <p className="small text-muted">{t("menu.fromCash")}</p>}
                {form.fromCatalog && <p className="small text-muted">{t("menu.fromCatalog")}</p>}
                <Row>
                  <Col md="5" className="mb-3">
                    <label>{t("menu.name")}</label>
                    <Input value={form.name} disabled={fromCash} onChange={(e) => set("name", e.target.value)} maxLength={200} />
                  </Col>
                  <Col md="2" className="mb-3">
                    <label>{t("menu.price")}</label>
                    <Input inputMode="decimal" value={form.price} disabled={fromCash} onChange={(e) => set("price", e.target.value)} placeholder="0,00" />
                  </Col>
                  <Col md="5" className="mb-3">
                    <label>{t("menu.category")}</label>
                    <Input list={`cats-${code}`} value={form.category} onChange={(e) => set("category", e.target.value)} placeholder={catPlaceholder} />
                    <datalist id={`cats-${code}`}>{categories.map((c) => <option key={c} value={c} />)}</datalist>
                  </Col>
                  <Col md="12" className="mb-3">
                    <label>{t("menu.description")}</label>
                    <Input type="textarea" rows="2" value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={2000}
                      placeholder={code === "food" ? t("menu.descPlaceholderFood") : t("menu.descPlaceholderOther")} />
                  </Col>
                  <Col md="12" className="mb-3">
                    <label className="d-block">{t("menu.allergensLabel")}</label>
                    <div className="d-flex flex-wrap" style={{ gap: "6px 18px" }}>
                      {ALLERGENS.map((k) => (
                        <label key={k} className="mb-0" style={{ cursor: "pointer" }}>
                          <input type="checkbox" checked={form.allergens.includes(k)}
                            onChange={(e) => set("allergens", e.target.checked ? [...form.allergens, k] : form.allergens.filter((a) => a !== k))} /> {t(`menu.allergens.${k}`)}
                        </label>
                      ))}
                    </div>
                  </Col>
                  {info.kcal && (
                    <Col md="2" className="mb-3">
                      <label>{t("menu.kcal")}</label>
                      <Input type="number" min="0" max="5000" value={form.kcal} onChange={(e) => set("kcal", e.target.value)} placeholder={t("menu.optional")} />
                    </Col>
                  )}
                  <Col md={info.kcal ? "10" : "12"} className="mb-3">
                    <label>{t("menu.photo")}</label>
                    <div className="d-flex align-items-center" style={{ gap: 12 }}>
                      {form.currentImage && !form.removeImage && !form.image && (
                        <img src={imgUrl(form.currentImage)} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8 }} />
                      )}
                      <Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => set("image", e.target.files?.[0] || null)} style={{ maxWidth: 360 }} />
                      {form.currentImage && !form.image && (
                        <label className="mb-0 small"><input type="checkbox" checked={form.removeImage} onChange={(e) => set("removeImage", e.target.checked)} /> {t("menu.removePhoto")}</label>
                      )}
                    </div>
                  </Col>
                </Row>
                <Button color="primary" onClick={save} disabled={saving}>{saving ? t("menu.saving") : t("menu.save")}</Button>
                <Button onClick={close} disabled={saving}>{t("menu.cancel")}</Button>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ gap: 12 }}>
                <CardTitle tag="h4" className="mb-0">{t("menu.listTitle", { title: secTitle, count: items.length })}</CardTitle>
                <div className="d-flex" style={{ gap: 8 }}>
                  <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t("menu.filter")} style={{ width: 200 }} />
                  {!form && <Button color="primary" className="m-0" onClick={() => setForm({ ...EMPTY })}>{t("menu.newButton", { item })}</Button>}
                </div>
              </div>
              {loading ? (
                <p>{t("menu.loading")}</p>
              ) : shown.length === 0 ? (
                <p className="text-muted mb-0">{items.length ? t("menu.noResults") : t("menu.noneOnList", { item })}</p>
              ) : (
                <Table responsive className="mb-0">
                  <thead>
                    <tr><th style={{ width: 64 }}></th><th>{t("menu.colName")}</th><th>{t("menu.colCategory")}</th><th className="text-right">{t("menu.colPrice")}</th><th>{t("menu.colStatus")}</th><th className="text-right">{t("menu.colActions")}</th></tr>
                  </thead>
                  <tbody>
                    {shown.map((d) => (
                      <tr key={d.uuid}>
                        <td>{d.image ? <img src={imgUrl(d.image)} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 8 }} /> : null}</td>
                        <td>
                          <b>{d.name}</b>
                          <div className="small text-muted">
                            {[d.source === "cic" ? "CassaInCloud" : "", d.beverage_dataset ? t("menu.tagCatalog") : "", (d.allergens || []).map((a) => t(`menu.allergens.${a}`, { defaultValue: a })).join(", ")].filter(Boolean).join(" · ")}
                          </div>
                        </td>
                        <td>{d.category || "—"}</td>
                        <td className="text-right text-nowrap">{eur(d.price)}</td>
                        <td>
                          <Badge color={d.is_available ? "success" : "secondary"}>{d.is_available ? t("menu.onList") : t("menu.soldOut")}</Badge>
                        </td>
                        <td className="text-right text-nowrap">
                          {d.source !== "cic" && (
                            <Button size="sm" color={d.is_available ? "secondary" : "primary"} className="m-0 mr-1" onClick={() => toggle(d)}>{d.is_available ? t("menu.soldOut") : t("menu.putBack")}</Button>
                          )}
                          <Button size="sm" className="m-0 mr-1" onClick={() => edit(d)}>{t("menu.edit")}</Button>
                          {d.source !== "cic" && (
                            <Button size="sm" color="danger" className="m-0" onClick={() => remove(d)}>×</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SectionMenuItems;
